import { useState } from "react";
import { reminderAPI } from "services";
import useUniversalStore from "store/universalStore";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);
import AddReminder from "forms/Reminder/AddReminder";
import { toast } from "sonner";
import { confirm } from "components";

const Reminder = () => {
  const { accountId, accounts, write } = useUniversalStore((state) => state);
  const account = accounts.find(acc => acc.id === accountId);

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);

  const reminder = account?.get("reminder");
  const enabled = reminder?.enabled;
  const reminderDateTime = reminder?.date;

  // overdue is true if reminder date and time less then current date and time
  const overDue = reminderDateTime && dayjs().isAfter(dayjs(reminderDateTime));

  //method to toggle Reminder
  const handleReminderToggle = async () => {
    if (!reminder) return;

    try {
      setIsLoading(true);
      const res = await reminderAPI.toggleReminder(accountId, !enabled);
      if (res) {
        write({ accounts: res })
      }
    } catch (error) {
      toast.error(error.message || "Failed to toggle the reminder!");
    } finally {
      setIsLoading(false);
    }
  };

  //method to delete the reminder
  const handleDeleteReminder = async () => {
    if (!reminder) return;
    try {
      setIsLoading(true);
      const response = await reminderAPI.deleteReminder(accountId);
      if (response) {
        write({ accounts: response })
      }
    } catch (error) {
      toast.error("Failed to delete the reminder!")
    } finally {
      setIsLoading(false);
    }
  };

  const paymentStatus = () => {
    if (overDue) {
      return (
        <div className="alert alert-danger d-flex align-items-center border-0 shadow-sm mb-4" role="alert">
          <i className="bi bi-exclamation-triangle-fill fs-4 me-3"></i>
          <div>
            <div className="fw-bold">Payment Overdue</div>
            <div className="small opacity-75">This payment was due on {dayjs(reminderDateTime).format("DD MMM, YYYY")}</div>
          </div>
        </div>
      )
    } else if (enabled) {
      return (
        <div className="alert alert-success d-flex align-items-center border-0 shadow-sm mb-4" role="alert">
          <i className="bi bi-clock-fill fs-4 me-3"></i>
          <div>
            <div className="fw-bold">Upcoming Payment</div>
            <div className="small opacity-75">Scheduled {dayjs(reminderDateTime).fromNow()}</div>
          </div>
        </div>
      )
    }
  }

  if (!reminderDateTime || isEditing)
    return <AddReminder
      existingAccount={account}
      onSuccess={() => setIsEditing(false)} />;

  return (
    <div className="reminder-container animate__animated animate__fadeIn px-md-3">
      <div className="card mb-4">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div className="d-flex align-items-center">
              <div className={`rounded-5 p-3 me-3 bg-secondary bg-opacity-10 text-secondary`}>
                <i className="bi bi-calendar-event fs-3"></i>
              </div>
              <div>
                <div className="text-muted small text-uppercase fw-bold tracking-wider">Scheduled Date</div>
                <div className={`fs-4 fw-bold text-dark`}>
                  {dayjs(reminderDateTime).format("DD-MMM-YY")}
                </div>
                <div className={`fw-medium`}>
                  {dayjs(reminderDateTime).format("hh:mm A")}
                </div>
              </div>
            </div>
            {!overDue ? (
              <div className="form-check form-switch p-0 m-0">
                <input
                  className="form-check-input ms-0 mt-0"
                  type="checkbox"
                  role="switch"
                  checked={enabled}
                  id="reminderToggle"
                  style={{ width: '3rem', height: '1.5rem', cursor: 'pointer' }}
                  onChange={() => !isLoading && handleReminderToggle()}
                  disabled={isLoading}
                />
              </div>) : null}
          </div>

          <hr className="my-4 opacity-10" />

          <div className="d-flex gap-2">
            <button
              className="btn btn-light border flex-grow-1 py-2 rounded-3 fw-bold d-flex align-items-center justify-content-center gap-2"
              onClick={() => !isLoading && setIsEditing(true)}
              disabled={isLoading}
            >
              <i className="bi bi-pencil-square"></i>
              Edit
            </button>
            <button
              className="btn btn-outline-danger flex-grow-1 py-2 rounded-3 fw-bold d-flex align-items-center justify-content-center gap-2"
              onClick={() => confirm({
                message: "You want to delete this <b>Reminder</b>",
                onConfirm: handleDeleteReminder,
              })}
              disabled={isLoading}
            >
              <i className="bi bi-trash"></i>
              Remove
            </button>
          </div>
        </div>
      </div>

      {paymentStatus()}

      {error && (
        <div className="text-center p-3 bg-warning bg-opacity-10 rounded-3 border border-warning border-opacity-25">
          <p className="text-warning-emphasis small m-0">
            <i className="bi bi-info-circle me-2"></i>
            {error}
          </p>
        </div>
      )}
    </div>
  );
}

export default Reminder;